// PARAQTA — entry point (ES module).
//
// Module-аралық dependency-лер тікелей импорт арқылы шешіледі.
// window.* expose тек:
//   - onTelegramAuth — Telegram widget callback (МІНДЕТТІ глобал)
//   - currentUser / isGuest / _dashData — module-дер арасында live state
//   - devLogin / enterGuest — Playwright smoke тесті үшін (тест pattern
//     міндетті click-pattern-ге өткенде жоюға болады)
//   - loginDone — Telegram OAuth result handler (window-ден шақырылмайды
//     енді, бірақ debug үшін window-да қалу қолайлы)

import './scripts/core/errlog.js';   // 02.11: экрандағы JS қателері → events (js_error) — бірінші тұруы керек
import { onTelegramAuth, loginDone } from './scripts/features/auth/index.js';
import { devLogin, enterGuest } from './scripts/features/auth/login-modal.js';
import { initApp } from './scripts/features/landing/index.js';
import { initPWA } from './scripts/features/pwa.js';
import { show } from './scripts/core/router.js';
import { openBookPage } from './scripts/features/book-page/index.js';
import { loadAppConfig } from './scripts/features/submissions/index.js';
import { track, captureRef } from './scripts/core/track.js';
import { initOfflineQueue } from './scripts/core/offline-queue.js';
import { initNative, IN_APP, mountWelcome } from './scripts/core/native.js';

// Side-effect imports: events.js глобал click delegation орнатады.
import './scripts/core/events.js';
import { journeyOn } from './scripts/core/journey.js';
import { trainOn } from './scripts/features/train/flag.js';
journeyOn();   // ?journey=1 / ?jdone=N адрес қайта жазылмай тұрып ұсталады (core/journey.js)

// ── State (cross-module read-only access — әзірге сақталады) ──
window.currentUser = null;
window.isGuest = false;
window.ws = null;
window.wsRetry = 0;

// ── Auth ──
window.onTelegramAuth = onTelegramAuth;
window.loginDone = loginDone;
window.devLogin = devLogin;
window.enterGuest = enterGuest;

// ── Playwright smoke test fixture-лері (тестілер `typeof X === 'function'`
// тексеретін болғандықтан window-да expose). Step 9 кезінде click pattern-ге
// ауыстырғанда жоюға болады. ──
window.show = show;
window.openBookPage = openBookPage;

// ── PWA: service worker register + install banner state ──
if (!IN_APP) initPWA();   // қолданбада «орнат» ұсынысы керек емес
// Офлайн кезек: байланыс үзілгенде сақталмай қалған оқу сессиялары
// интернет қайтқанда автоматты жіберіледі (оқыған уақыт жоғалмайды).
initOfflineQueue();
// Қолданба (iOS/Android) ішінде ғана: кіру, виджет, Live Activity, push. Браузерде ештеңе істемейді.
if (IN_APP) initNative();
else if (/[?&]welcome=preview/.test(location.search)) mountWelcome();   // А1 алғашқы экранын браузерде көру

// ── Бастапқы init: auth check + landing/dashboard көрсету ──
// Оқу беті v3 (FocusTown жолы) — тек жалаумен (?oqu3=1), әзірге founder-ге ғана. Өшік болса модуль жүктелмейді.
function oqu3On() {
  // 19.10 founder: ескі көрініс ешкімге ашылмасын — ?oqu3=0 / pq_oqu3='0' енді әсер етпейді (белгі тазаланады).
  try {
    if (localStorage.getItem('pq_oqu3') === '0') localStorage.setItem('pq_oqu3', '1');
    if (localStorage.getItem('pq_dukan2') === null) localStorage.setItem('pq_dukan2', '1');   // 3D дүкен де бірге
  } catch (_) {}
  return true;
}
window.addEventListener('DOMContentLoaded', async () => {
  if (oqu3On()) import('./scripts/features/oqu3/index.js').then(m => m.initOqu3()).catch(() => {});
  import('./scripts/features/oqu3/duration.js').then(m => m.initDuration()).catch(() => {});   // 29.10: шам 25–60 мин, бәріне
  if (trainOn()) import('./scripts/features/train/index.js').then(m => m.initTrain()).catch(() => {});   // ?train=1: 22:00 пойызы (әдепкі өшік)
  // Аналитика: сайтқа кіру (баспа трафигі — қонақ/тіркелген бәрі)
  try { track('visit'); } catch (_) {}

  // ── Telegram Mini App: АВТО-логин (initData) ──────────────────
  // Егер PARAQTA Telegram ІШІНДЕ ашылса — парол/виджет жоқ, Telegram
  // кимлігі initData-мен серверде тексеріліп, сессия ашылады. Одан
  // кейін initApp() /api/me-ден кірген қолданушыны көреді.
  try {
    const _wa = window.Telegram && window.Telegram.WebApp;
    if (_wa && _wa.initData && _wa.initData.length > 0) {
      try { _wa.ready(); _wa.expand(); } catch (_) {}
      document.body.classList.add('pq-miniapp');
      // Толық экран (Dogs/ойындар секілді) — Bot API 8.0+. Ескі клиентте
      // requestFullscreen жоқ → guard. Жоғарғы status bar/ойыққа орын береміз.
      try {
        if (typeof _wa.requestFullscreen === 'function') {
          const _applySafe = () => {
            try {
              const s = _wa.safeAreaInset || {}, c = _wa.contentSafeAreaInset || {};
              document.documentElement.style.setProperty('--tg-safe-top', ((s.top || 0) + (c.top || 0)) + 'px');
              document.body.classList.add('pq-tg-fs');
            } catch (_) {}
          };
          _wa.requestFullscreen();
          _applySafe();
          try { _wa.onEvent('fullscreenChanged', _applySafe); } catch (_) {}
          try { _wa.onEvent('safeAreaChanged', _applySafe); } catch (_) {}
          try { _wa.onEvent('contentSafeAreaChanged', _applySafe); } catch (_) {}
        }
      } catch (_) {}
      await fetch('/api/auth/tg-webapp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ init_data: _wa.initData }),
      }).catch(() => {});
    }
  } catch (_) {}
  // Stage 7.5: сақталған шрифт өлшемін қалпына келтіру (accessibility, zoom CSS)
  try {
    const scale = parseInt(localStorage.getItem('paraqta:font_scale') || '100', 10);
    if (scale && scale >= 80 && scale <= 200 && scale !== 100) {
      document.body.style.zoom = (scale / 100).toString();
    }
  } catch (_) {}

  // Step BP4: config (allow_user_submissions, is_admin) жүктеу — body
  // класы арқылы CSS-та "Кітабым жоқ" батырма + admin nav-tab visibility
  // басқарады. initApp-тен бұрын жүктесек race условиясыз.
  loadAppConfig();

  // Ф3: Доспен оқу шақыру сілтемесі — /f/{code} → кодты сақтап, логин
  // болғаннан кейін (initApp) қабылдаймыз. URL тазаланады.
  // Оқу шеңберіне шақыру: paraqta.kz/c/КОД (мұғалім/ата-ана осыны жібереді).
  // Бұрын код тек «Бірге» бетіндегі батырманы тауып, қолмен теруге болатын —
  // оқушы оны таппайтын. Енді сілтемені бассa, қосылу экраны өзі шығады.
  const circleMatch = window.location.pathname.match(/^\/c\/([A-Za-z0-9]{4,12})/);
  if (circleMatch) {
    try { localStorage.setItem('pq_circle_code', circleMatch[1].toUpperCase()); } catch (_) {}
    try { history.replaceState({}, '', '/'); } catch (_) {}
  }

  // 24.08: ашық клуб бетінен шақыру — /?join_club={id}. Кодты сақтаймыз,
  // Клубтар беті ашылғанда қосылу автоматты жүреді (groups/loadGroups).
  try {
    const _qs = new URLSearchParams(window.location.search);
    // 27.08: клуб шақыру сілтемесі — /?join=КОД. Бұрын шақыру
    // t.me/oyordabot-қа апаратын, яғни клубқа тек Telegram арқылы кіруге
    // болатын. Енді сілтеме сайттың өзінде ашылып, бір басумен қосады.
    const _jcode = _qs.get('join');
    if (_jcode && /^[A-Za-z0-9_-]{4,64}$/.test(_jcode)) {
      localStorage.setItem('pq_join_code', _jcode);
      history.replaceState({}, '', '/');
    }
    const _jc = _qs.get('join_club');
    if (_jc && /^\d{1,12}$/.test(_jc)) {
      localStorage.setItem('pq_join_club', _jc);
      history.replaceState({}, '', '/');
    }
    // 01.09: мұғалім сілтемесі (/mugalim → ?mugalim=1) — логиннен кейін
    // сынып ашу рұқсаты өзі беріледі, founder-ге хабар кетеді.
    if (_qs.has('mugalim')) {
      localStorage.setItem('pq_mugalim', '1');
      history.replaceState({}, '', '/');
    }
    // 01.09: сынып коды (/c/КОД → ?synyp=КОД). Бұған дейін /c/ сілтемесі
    // ӨЛІ еді. ❗Авто-қосылу ЖОҚ: кілт `pq_circle_code` — circle модулінің
    // КЕЛІСІМ картасы оқиды («иесі нені көреді / көрмейді» + «Қосылу»
    // батырмасы). Оқушы не көрінетінін біліп барып қосылады.
    const _sc = _qs.get('synyp');
    if (_sc && /^[A-Za-z0-9-]{4,12}$/.test(_sc)) {
      localStorage.setItem('pq_circle_code', _sc.toUpperCase());
      history.replaceState({}, '', '/');
    }
  } catch (_) {}

  const friendMatch = window.location.pathname.match(/^\/f\/([A-Za-z0-9_-]{1,32})/);
  if (friendMatch) {
    // localStorage — логин редиректінен кейін де сақталады (қабылданғанша тұрады).
    try { localStorage.setItem('pq_friend_code', friendMatch[1]); } catch (_) {}
    try { history.replaceState({}, '', '/'); } catch (_) {}
  }

  // 23.10 «Досыңа орын ұста»: /s/КОД → бөлмеде досының қасындағы сақталған орын (features/dukan/seat-invite.js)
  const seatInvMatch = window.location.pathname.match(/^\/s\/([A-Za-z0-9_-]{4,32})/);
  if (seatInvMatch) {
    try { localStorage.setItem('pq_seat_inv', seatInvMatch[1]); } catch (_) {}
    try { history.replaceState({}, '', '/'); } catch (_) {}
  }

  // Ф3 fix: доспен-оқу шақыруы. Логин бар → достық құрамыз; жоқ → шақыру экраны + кіру.
  async function _handleFriendInvite() {
    let fc = null;
    try { fc = localStorage.getItem('pq_friend_code'); } catch (_) {}
    if (!fc) return;
    if (window.currentUser) {
      try {
        const d = await fetch('/api/friends/accept', {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: fc }),
        }).then(r => r.json());
        try { localStorage.removeItem('pq_friend_code'); } catch (_) {}
        if (d && d.ok) {
          import('./scripts/utils/toast.js').then(m => m.toast('🤝 ' + (d.friend || 'Дос') + '-пен оқу серігі болдың!')).catch(() => {});
          try { show('groups'); } catch (_) {}
        }
      } catch (_) {}
    } else {
      let owner = 'Досың';
      try {
        const info = await fetch('/api/friends/invite-info?code=' + encodeURIComponent(fc)).then(r => r.json());
        if (info && info.owner_name) owner = info.owner_name;
      } catch (_) {}
      _showFriendWelcome(owner);
    }
  }

  function _showFriendWelcome(name) {
    if (document.getElementById('pqFriendWelcome')) return;
    const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const ov = document.createElement('div');
    ov.id = 'pqFriendWelcome';
    ov.style.cssText = 'position:fixed;inset:0;z-index:9000;background:rgba(20,17,12,.82);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px;';
    ov.innerHTML =
      '<div style="max-width:340px;width:100%;background:#1F1A13;border:1px solid #3A3019;border-radius:22px;padding:30px 24px;text-align:center;color:#ECE5D4;font-family:\'Onest\',system-ui,sans-serif;">' +
        '<div style="font-size:44px;margin-bottom:10px;">🕯</div>' +
        '<div style="font-family:\'Cormorant Garamond\',Georgia,serif;font-size:24px;font-weight:600;line-height:1.2;">' + esc(name) + ' сені PARAQTA-ға шақырды</div>' +
        '<div style="font-size:14px;color:#B7A98A;margin-top:10px;line-height:1.5;">Бірге оқысаңдар — әдет үзілмейді. Кіріп, оқу серігі бол.</div>' +
        '<button data-action="show-login" style="width:100%;margin-top:22px;border:none;border-radius:14px;background:linear-gradient(160deg,#F0B254,#DA942F);color:#2A1E0B;font-size:15px;font-weight:700;padding:15px;cursor:pointer;font-family:inherit;">🕯 Кіріп бастау</button>' +
        '<button id="pqFwClose" style="width:100%;margin-top:9px;background:none;border:none;color:#8A8070;font-size:13px;cursor:pointer;padding:8px;">Кейінірек</button>' +
      '</div>';
    document.body.appendChild(ov);
    const loginBtn = ov.querySelector('[data-action="show-login"]');
    if (loginBtn) loginBtn.addEventListener('click', () => { ov.remove(); });
    const closeBtn = ov.querySelector('#pqFwClose');
    if (closeBtn) closeBtn.addEventListener('click', () => ov.remove());
  }

  // Stage 6.9.4: /u/{slug} path → Public profile (Coming Soon bypass).
  // SPA route — Nginx `try_files` /index.html-ге fall back жасайды,
  // одан соң мына JS path-ты detect жасап, тиісті pageді ашады.
  const pubMatch = window.location.pathname.match(/^\/u\/([^/]+)/);
  if (pubMatch) {
    document.getElementById('landingPage')?.setAttribute('hidden', '');
    document.getElementById('app')?.style && (document.getElementById('app').style.display = 'block');
    show('public-profile');
    return;
  }

  // Блогер реф сілтемесі: /b/{code} немесе ?ref={code} → ұста + марафон бетін аш.
  // (URL track.js ішінде тазаланады; /u/ публик-профиль үлгісіндей bypass.)
  const refCode = captureRef();
  if (refCode) {
    document.getElementById('landingPage')?.setAttribute('hidden', '');
    const _appEl = document.getElementById('app');
    if (_appEl) _appEl.style.display = 'block';
    // Реф қақпасы (NFC flywheel секілді): тіркелмесе тек марафон беті,
    // сайтқа кірмейді/төлей алмайды. refEntry auth тексеріп шешеді.
    import('./scripts/features/marathon/index.js').then(m => m.refEntry(refCode)).catch(() => {});
    return;
  }

  // PARAQTA тікелей марафон сілтемесі: paraqta.kz/marathon → қақпалы марафон беті
  // + тіркеу авто-ашылады (максималды жеңіл). Реф коды жоқ (PARAQTA-ның өз трафигі).
  if (window.location.pathname.replace(/\/+$/, '') === '/marathon') {
    document.getElementById('landingPage')?.setAttribute('hidden', '');
    const _appEl = document.getElementById('app');
    if (_appEl) _appEl.style.display = 'block';
    try { history.replaceState({}, '', '/'); } catch (_) {}
    // Жалпы марафон беті = PARAQTA трафигі → блогер «ұсынады» белгісін көрсетпейміз.
    try { sessionStorage.removeItem('pq_ref_show'); } catch (_) {}
    import('./scripts/features/marathon/index.js').then(m => m.refEntry(null, true)).catch(() => {});
    return;
  }

  // Re-engagement push: /room → Оқу бетін (бөлме лобби) ашады — NFC авто-бастаусыз.
  // Веб-push «N дос оқып отыр» осында апарады (сайтта, тура бөлмеге).
  if (window.location.pathname.replace(/\/+$/, '') === '/room') {
    document.getElementById('landingPage')?.setAttribute('hidden', '');
    const _re = document.getElementById('app');
    if (_re) _re.style.display = 'block';
    try { history.replaceState({}, '', '/'); } catch (_) {}
    initApp().then(() => { try { show('oqu'); } catch (_) {} }).catch(() => {});
    return;
  }

  // NFC бетбелгі: /oqu немесе ?nfc/?c → оқу сессиясын аш. ?c=slot → чип слоты.
  const _sp = new URLSearchParams(window.location.search);
  const _nfc = (window.location.pathname.replace(/\/+$/, '') === '/oqu') || _sp.has('nfc') || _sp.has('c');
  if (_nfc) {
    const _slot = (_sp.get('c') || '1').slice(0, 16);
    document.getElementById('landingPage')?.setAttribute('hidden', '');
    const _appEl = document.getElementById('app');
    if (_appEl) _appEl.style.display = 'block';
    try { history.replaceState({}, '', '/'); } catch (_) {}
    import('./scripts/features/oqu/index.js').then(m => m.nfcEntry(_slot)).catch(() => {});
    return;
  }

  initApp().then(() => {
    // Ф3: доспен оқу шақыруы — ЛОГИН БАР/ЖОҚ екеуін де ұстаймыз (воронка ағуын жою).
    _handleFriendInvite();
    import('./scripts/features/dukan/seat-invite.js').then(m => m.handleSeatInvite({ show })).catch(() => {});
    // ?login=1 → логин терезесін БІРДЕН ашамыз. Бөлме шақыру бетіндегі
    // «Telegram арқылы кіру» батырмасы осында әкеледі: бұрын жай ғана «/»
    // болатын да, адам басты бетке түсіп «қайта бірінші бетке жіберді»
    // деп ойлайтын (пикник күні founder байқады).
    if (!window.currentUser) {
      try {
        if (new URLSearchParams(location.search).has('login')) {
          history.replaceState({}, '', '/');
          import('./scripts/features/auth/login-modal.js')
            .then(m => m.showLoginModal()).catch(() => {});
        }
      } catch (_) {}
      return;
    }
    // Онбординг: ?onboarding → мәжбүрлеп (founder тесті); әйтпесе жаңа қолданушыға авто.
    const _forceOnb = new URLSearchParams(location.search).has('onboarding');
    // 05.09: сынып сілтемесімен (/c/КОД) енді ғана кірген оқушыға АЛДЫМЕН
    // «Бірге» бетіндегі келісім картасы көрінсін — онбординг оны жауып,
    // оқу бетіне алып кетпесін. Онбординг келесі кіргенде өзі шығады
    // (backend `needs` сол күйінде). Қақпаны ашамыз — басқа оверлей күтпесін.
    let _pendClass = false;
    try { _pendClass = !_forceOnb && !!localStorage.getItem('pq_circle_code'); } catch (_) {}
    if (_pendClass) {
      import('./scripts/core/ui-queue.js').then(m => m.openGate && m.openGate()).catch(() => {});
    } else {
      // ?journey=1 (core/journey.js): ескі «Не оқисың?» онбординг ОРНЫНА — кітапты бір-ақ рет кітапханашы сұрайды.
      // openGate() МІНДЕТТІ: әйтпесе ui-queue кезегі 12 с тұрып қалады. ?onboarding (founder тесті) бұрынғыдай.
      const _oldOnb = () => import('./scripts/features/onboarding/index.js').then(m => m.initOnboarding(_forceOnb)).catch(() => {});
      if (!_forceOnb && journeyOn()) {
        import('./scripts/core/journey.js').then(j => j.journeyInit()).catch(() => {})
          .finally(() => import('./scripts/core/ui-queue.js').then(m => m.openGate && m.openGate()).catch(() => {}));
      } else _oldOnb();
    }
  }).catch(() => {});
  setTimeout(_maybeOpenBookshelf, 900);
  // Марафонға тіркелген, бірақ Telegram-чатта ЖОҚ адамға «Чатқа кір»
  // баннерін көрсету (currentUser жүктелген соң, лоадты бөгемей).
  setTimeout(() => {
    if (!window.currentUser) return;
    import('./scripts/features/marathon/index.js')
      .then(m => m.initChatBanner && m.initChatBanner()).catch(() => {});
  }, 1800);
});

// Stage 8: жеке сөре тест кіреберісі — #kitaptarym (backend admin/flag-gated).
function _maybeOpenBookshelf() {
  if (location.hash === '#kitaptarym') {
    import('./scripts/features/bookshelf/index.js')
      .then(m => m.openBookshelf()).catch(() => {});
  }
}
window.addEventListener('hashchange', _maybeOpenBookshelf);
